import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  TvControlledDropdown,
  TvDropdownToggle,
  TvDropdownMenu,
  TvDropdownItem,
  TvImg,
  TvSpan,
  TvI,
} from "@trustvid/design-system";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useAccountBrandingContext, useActiveAccountId } from "../../../context/account-branding-context";
import { resolveAccountAvatarDisplayUrl } from "../../../utils/account-branding-theme";
import { clearClientSessionCaches } from "../../../core/cache/clearClientSessionCaches";
import { buildScopedPath } from "../../../utils/account-context";
import { clearUserSession } from "../../../utils/auth-helpers";
import { ROUTE_PATHS } from "../../../routes/path-map";

import trustvidLogo from "../../../assets/images/trustvid-logo.png";
import withRouter from "../../Common/withRouter";
import MFASettings from "../../../features/pages/Authentication/MFASettings";

const isLegacyPlaceholderAvatar = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;
  return /(^|\/)avatar-\d+\.(jpg|jpeg|png|webp)(\?.*)?$/.test(normalized);
};

const ProfileMenu = (props) => {
  const [menu, setMenu] = useState(false);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const { branding } = useAccountBrandingContext();
  const activeAccountId = useActiveAccountId();

  const [userData, setUserData] = useState(() => JSON.parse(localStorage.getItem("userdata") || "{}"));
  const [accountLogo, setAccountLogo] = useState("");
  const name = userData?.name;

  const syncAccountLogoFromSession = useCallback((event) => {
    const nextUserData = JSON.parse(localStorage.getItem("userdata") || "{}");
    const updatedAt = event?.detail?.updatedAt || Date.now();
    if (!activeAccountId || String(nextUserData?.account?.id) !== String(activeAccountId)) {
      setAccountLogo("");
      return;
    }
    const logo = event?.detail?.account?.logo || nextUserData?.account?.logo || "";
    if (!logo) {
      setAccountLogo("");
      return;
    }
    const sep = logo.includes("?") ? "&" : "?";
    setAccountLogo(`${logo}${sep}v=${updatedAt}`);
  }, [activeAccountId]);

  useEffect(() => {
    setUserData(JSON.parse(localStorage.getItem("userdata") || "{}"));
  }, [props.success]);

  useEffect(() => {
    syncAccountLogoFromSession();
  }, [syncAccountLogoFromSession]);

  useEffect(() => {
    const handleAccountProfileUpdated = (event) => {
      const nextUserData = JSON.parse(localStorage.getItem("userdata") || "{}");
      setUserData(nextUserData);
      syncAccountLogoFromSession(event);
    };

    window.addEventListener("tv:account-profile-updated", handleAccountProfileUpdated);
    return () => {
      window.removeEventListener("tv:account-profile-updated", handleAccountProfileUpdated);
    };
  }, [syncAccountLogoFromSession]);

  const profileAvatarSrc = (() => {
    const resolved = resolveAccountAvatarDisplayUrl(branding, accountLogo);
    if (!resolved || isLegacyPlaceholderAvatar(resolved)) return trustvidLogo;
    return resolved;
  })();
  const canOpenAdminTools = userData?.role === "SuperAdmin" || userData?.role === "Admin";
  const isSuperAdmin = userData?.role === "SuperAdmin";
  const settingsPath = activeAccountId
    ? buildScopedPath(activeAccountId, ROUTE_PATHS.accountSettings)
    : ROUTE_PATHS.accountSettings;
  const superAdminSettingsPath = activeAccountId
    ? buildScopedPath(activeAccountId, ROUTE_PATHS.superAdminSettings)
    : ROUTE_PATHS.superAdminSettings;
  const brandingPath = activeAccountId
    ? buildScopedPath(activeAccountId, ROUTE_PATHS.branding)
    : ROUTE_PATHS.branding;

  const LogoutHandler = () => {
    // Important: clear stored `accountId`/`userType` too, otherwise API calls keep using the previous tenant.
    clearUserSession();
    clearClientSessionCaches();
    toast.success("Logout successfully!");
  };

  return (
    <>
      <TvControlledDropdown isOpen={menu} toggle={() => setMenu(!menu)} className="d-inline-block">
        <TvDropdownToggle className="btn header-item" id="page-header-user-dropdown" tag="button">
          <TvImg className="rounded-circle header-profile-user" src={profileAvatarSrc} alt="Avatar" />
          <TvSpan className="d-none d-xl-inline-block ms-2 me-1">{name}</TvSpan>
          <TvI className="mdi mdi-chevron-down d-none d-xl-inline-block" />
        </TvDropdownToggle>
        <TvDropdownMenu className="dropdown-menu-end">
          {canOpenAdminTools ? (
            <>
              <Link to={settingsPath} className="dropdown-item" data-testid="profile-menu-account-settings">
                <TvI className="bx bx-cog font-size-16 align-middle me-1" />
                <TvSpan>{props.t("Account Settings")}</TvSpan>
              </Link>
              {isSuperAdmin ? (
                <Link
                  to={superAdminSettingsPath}
                  className="dropdown-item"
                  data-testid="profile-menu-super-admin-settings"
                >
                  <TvI className="bx bx-wrench font-size-16 align-middle me-1" />
                  <TvSpan>{props.t("Super Admin Settings")}</TvSpan>
                </Link>
              ) : null}
              <Link to={brandingPath} className="dropdown-item" data-testid="profile-menu-branding">
                <TvI className="bx bx-palette font-size-16 align-middle me-1" />
                <TvSpan>{props.t("Branding")}</TvSpan>
              </Link>
            </>
          ) : null}
          <TvDropdownItem tag="a" onClick={() => setMfaModalOpen(true)}>
            <TvI className="bx bx-shield-quarter font-size-16 align-middle me-1" />
            {props.t("Enable MFA")}
          </TvDropdownItem>
          <Link to="/logout" className="dropdown-item" onClick={LogoutHandler}>
            <TvI className="bx bx-power-off font-size-16 align-middle me-1 text-danger" />
            <TvSpan>{props.t("Logout")}</TvSpan>
          </Link>
        </TvDropdownMenu>
      </TvControlledDropdown>

      <MFASettings isOpen={mfaModalOpen} toggle={() => setMfaModalOpen(!mfaModalOpen)} user={userData} />
    </>
  );
};

ProfileMenu.propTypes = {
  success: PropTypes.any,
  t: PropTypes.any,
};

const mapStateToProps = (state) => {
  const { error, success } = state.Profile;
  return { error, success };
};

export default withRouter(connect(mapStateToProps, {})(withTranslation()(ProfileMenu)));
